// JobClose AI · Slice 2T · Levi Project Snapshot
// -----------------------------------------------
// Read-only Levi presentation card mounted at the top of the Project
// Workspace. Consumes ONLY data that ProjectDetail already fetched. No
// network calls. No mutations. No LLM calls. No new state store.
//
// The card reuses the shared Levi drawer via `useLeviDrawer()` from
// LeviLauncher.jsx so contractors can pivot topics freely — the
// snapshot itself is never Levi's full intelligence.

import { Link } from "react-router-dom";
import { AlertTriangle, MessageCircleQuestion, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useLeviDrawer } from "@/components/LeviLauncher";
import {
  deriveLeviFacts,
  deriveLeviRecommendations,
  deriveLeviAvailableActions,
} from "@/lib/leviIntel";

const _toneStyles = {
  warning: {
    icon: AlertTriangle,
    iconClass: "text-[var(--jc-red)]",
    borderClass: "border-l-[var(--jc-red)]",
  },
  action: {
    icon: Sparkles,
    iconClass: "text-[var(--jc-blue)]",
    borderClass: "border-l-[var(--jc-blue)]",
  },
  info: {
    icon: MessageCircleQuestion,
    iconClass: "text-[var(--jc-text-soft)]",
    borderClass: "border-l-[var(--jc-border-strong)]",
  },
  positive: {
    icon: CheckCircle2,
    iconClass: "text-[var(--jc-green)]",
    borderClass: "border-l-[var(--jc-green)]",
  },
};

const _renderCopy = (rec, t) => {
  if (!rec || !rec.copy_key) return "";
  const args = rec.copy_args || {};
  // Some translations accept structured args (count, names, etc).
  // We pass them positionally-first, then as a single object, so the
  // dictionary can use either signature.
  try {
    return t(rec.copy_key, args);
  } catch (_) {
    return t(rec.copy_key);
  }
};

const RecommendationRow = ({ rec, action, t, onHandler }) => {
  const tone = _toneStyles[rec.tone] || _toneStyles.info;
  const Icon = tone.icon;
  const label = _renderCopy(rec, t);

  const cta = action ? (
    action.kind === "in_page_handler" ? (
      <button
        type="button"
        onClick={() => onHandler(action.handler_id)}
        data-testid={action.testid}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
      >
        {t(action.label_key)} <ArrowRight size={12} />
      </button>
    ) : (
      <Link
        to={action.to}
        data-testid={action.testid}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
      >
        {t(action.label_key)} <ArrowRight size={12} />
      </Link>
    )
  ) : null;

  return (
    <div
      className={`flex items-start gap-2.5 py-1.5`}
      data-testid={`levi-rec-${rec.id}`}
    >
      <Icon size={14} className={`shrink-0 mt-0.5 ${tone.iconClass}`} />
      <div className="flex-1 min-w-0 text-sm text-[var(--jc-text)]">{label}</div>
      {cta && <div className="shrink-0">{cta}</div>}
    </div>
  );
};

/**
 * Props:
 *   - project, estimates, proposals, contracts, invoices, workOrders,
 *     closeoutPackets  → all pre-loaded by ProjectDetail.
 *   - handlers  → { open_intake_edit_panel: fn, generate_closeout_packet: fn }
 */
export default function LeviProjectSnapshot({
  project,
  estimates,
  proposals,
  contracts,
  invoices,
  workOrders,
  closeoutPackets,
  handlers,
}) {
  const { t } = useLang();
  const { openDrawer } = useLeviDrawer();

  if (!project) return null;

  const facts = deriveLeviFacts({
    context: "project_workspace",
    project,
    estimates,
    proposals,
    contracts,
    invoices,
    workOrders,
    closeoutPackets,
  });

  const recs = deriveLeviRecommendations(facts);
  const actions = deriveLeviAvailableActions(facts, recs, {
    projectId: project.id,
    proposals,
    contracts,
    invoices,
  });

  const primary = recs.primary;
  const primaryAction = primary && primary.action_id ? actions.by_id[primary.action_id] : null;
  const askLevi = actions.ask_levi_action;

  const handleHandler = (handlerId) => {
    if (!handlerId) return;
    const fn = handlers && handlers[handlerId];
    if (typeof fn === "function") fn();
  };

  const tone = _toneStyles[primary?.tone] || _toneStyles.info;

  return (
    <section
      data-testid="levi-project-snapshot"
      className={`jc-card p-5 mb-6 border-l-4 ${tone.borderClass}`}
    >
      <div className="flex items-start gap-4">
        <img
          src="/levi/levi-badge.webp"
          alt="Levi"
          data-testid="levi-project-snapshot-avatar"
          className="w-14 h-14 shrink-0 rounded-full bg-[var(--jc-surface)] p-0.5 border border-[var(--jc-border)] object-cover"
          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        />
        <div className="flex-1 min-w-0">
          <div className="overline">{t("levi_project_snapshot.header_kicker")}</div>
          <div className="font-display text-lg leading-tight" data-testid="levi-project-snapshot-header">
            {t("levi_project_snapshot.header")}
          </div>

          {/* Levi recommends */}
          <div className="mt-3">
            <div className="overline text-[var(--jc-text-soft)] mb-1">
              {t("levi_project_snapshot.section_recommends")}
            </div>
            <div data-testid="levi-primary-recommendation">
              <RecommendationRow
                rec={primary}
                action={primaryAction}
                t={t}
                onHandler={handleHandler}
              />
            </div>
          </div>

          {/* Also noticed */}
          {recs.also_noticed.length > 0 && (
            <div className="mt-3">
              <div className="overline text-[var(--jc-text-soft)] mb-1">
                {t("levi_project_snapshot.section_also_noticed")}
              </div>
              <ul data-testid="levi-also-noticed" className="divide-y divide-[var(--jc-border)]">
                {recs.also_noticed.map((rec) => {
                  const act = rec.action_id ? actions.by_id[rec.action_id] : null;
                  return (
                    <li key={rec.id} data-testid={`levi-also-item-${rec.id}`}>
                      <RecommendationRow
                        rec={rec}
                        action={act}
                        t={t}
                        onHandler={handleHandler}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Warnings — surfaced separately from the "also noticed" list
              when there are genuinely risky conditions distinct from the
              primary. Primary already surfaces the top warning. */}
          {recs.warnings.length > 1 && (
            <div className="mt-3">
              <div className="overline text-[var(--jc-red)] mb-1" data-testid="levi-warnings-header">
                {t("levi_project_snapshot.section_warnings")}
              </div>
              <ul data-testid="levi-warnings" className="divide-y divide-[var(--jc-border)]">
                {recs.warnings
                  .filter((w) => !primary || w.id !== primary.id)
                  .map((rec) => {
                    const act = rec.action_id ? actions.by_id[rec.action_id] : null;
                    return (
                      <li key={rec.id} data-testid={`levi-warning-item-${rec.id}`}>
                        <RecommendationRow
                          rec={rec}
                          action={act}
                          t={t}
                          onHandler={handleHandler}
                        />
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {/* Ask Levi */}
          <div className="mt-4 pt-4 border-t border-[var(--jc-border)]">
            <button
              type="button"
              onClick={openDrawer}
              data-testid={askLevi.testid}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md jc-btn-secondary"
            >
              <MessageCircleQuestion size={14} />
              {t(askLevi.label_key)}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
